import { useState, useEffect } from 'react';
import { useQuery } from '../../hooks/useQuery';
import { useAuth } from '../../hooks/useAuth';
import { Breadcrumbs } from '../../components/navigation/Breadcrumbs';
import { Loader2, Users, Search, Heart, Gift, Zap, Info, Award, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CopyableFriendCode } from '../../components/friend-codes/CopyableFriendCode';
import { PageTitle } from '../../components/layout/PageTitle';
import { PageLayout } from '../../components/layout/PageLayout';
import { SEO } from '../../components/seo/SEO';
import { Helmet } from 'react-helmet-async';
import { useI18n } from '../../contexts/I18nContext';

interface PublicFriendCode {
  user_id: string;
  username: string;
  pokemon_id: string;
  pokemon_id_description: string;
  is_pokemon_id_public: boolean;
  is_collection_public: boolean;
  avatar_url: string | null;
}

export function FriendCodes() {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showTips, setShowTips] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const { t, getPathForLang } = useI18n();
  
  const { data: friendCodes, loading } = useQuery<PublicFriendCode[]>(
    'public_friend_codes',
    {},
    { withRelations: '*' }
  );
  
  // Dédupliquer et filtrer les codes amis
  const filteredFriendCodes = friendCodes 
    ? friendCodes
      .filter(code => 
        code.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        code.pokemon_id.includes(searchTerm) ||
        code.pokemon_id_description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  // Calculer les indices pour la pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentFriendCodes = filteredFriendCodes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredFriendCodes.length / itemsPerPage);

  // Fonctions de navigation entre les pages
  const goToPage = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    // Scroll vers le haut de la liste quand on change de page
    window.scrollTo({
      top: document.getElementById('friend-codes-list')?.offsetTop || 0,
      behavior: 'smooth'
    });
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  // Animation pour les conseils
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTips(true);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  // Réinitialiser la page courante lors d'une recherche
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Schéma JSON-LD pour le SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": t('friend_codes.seo.title'),
    "description": t('friend_codes.seo.description'),
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": filteredFriendCodes.map((code, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Person",
          "name": code.username,
          "identifier": code.pokemon_id
        }
      }))
    }
  };

  if (loading) {
    return (
      <PageLayout disableAds={true}>
        <SEO 
          title={t('friend_codes.loading')}
          description={t('friend_codes.loading_message')}
        />
        <div className="flex justify-center items-center min-h-[calc(100vh-4rem)]" aria-label={t('friend_codes.loading')}>
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" aria-hidden="true" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout disableAds={true}>
      <SEO 
        title={t('friend_codes.seo.title')}
        description={t('friend_codes.seo.description')}
        og_image="/images/friend-codes-share.jpg"
      />
      
      {/* JSON-LD pour le référencement */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageTitle title={t('friend_codes.title')} />
        
        {/* Hero Section avec bannière */}
        <section className="relative mb-12 bg-gradient-to-r from-indigo-700 to-purple-700 rounded-2xl overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-[url('/images/pokeballs-pattern.png')] bg-repeat"></div>
          <div className="relative z-10 px-8 py-12 text-white">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">{t('friend_codes.hero.title')}</h1>
            <p className="text-xl opacity-90 max-w-2xl mb-6">
              {t('friend_codes.hero.description')}
            </p>
            <div className="flex flex-wrap gap-4 mt-4">
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg flex items-center gap-2">
                <Users className="w-5 h-5" />
                <span>{filteredFriendCodes.length} {t('friend_codes.hero.stats.codes_available')}</span>
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg flex items-center gap-2">
                <Heart className="w-5 h-5" />
                <span>{t('friend_codes.hero.stats.active_community')}</span>
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg flex items-center gap-2">
                <Gift className="w-5 h-5" />
                <span>{t('friend_codes.hero.stats.shop_tickets')}</span>
              </div>
            </div>
          </div>
        </section>
        
        <section className="mb-10">
          <Breadcrumbs items={[{ label: t('friend_codes.breadcrumbs.friend_codes'), path: getPathForLang('/friend-codes') }]} />
          
          <div className="mt-8 bg-white rounded-xl shadow-sm p-6 mb-8">
            <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{t('friend_codes.search_section.title')}</h2>
                <p className="mt-2 text-gray-600">
                  {t('friend_codes.search_section.description')}
                </p>
              </div>
              
              <div className="relative">
                <label htmlFor="search-friend-codes" className="sr-only">{t('friend_codes.search_section.search_label')}</label>
                <input
                  id="search-friend-codes"
                  type="search"
                  placeholder={t('friend_codes.search_section.placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64 pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                  aria-label={t('friend_codes.search_section.search_label')}
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" aria-hidden="true" />
              </div>
            </header>
          </div>
          
          {/* Conseils d'utilisation */}
          <div className={`bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8 transition-all duration-500 ${showTips ? 'opacity-100' : 'opacity-0'}`}>
            <div className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <h2 className="text-lg font-semibold text-amber-800">{t('friend_codes.tips.title')}</h2>
                <p className="mt-2 text-amber-700">
                  {t('friend_codes.tips.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="friend-codes-list" aria-label={t('friend_codes.title')} className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700">
              {filteredFriendCodes.length > 0 
                ? `${t('friend_codes.list_section.showing')} ${indexOfFirstItem + 1}-${Math.min(indexOfLastItem, filteredFriendCodes.length)} ${t('friend_codes.list_section.on')} ${filteredFriendCodes.length} ${t('friend_codes.list_section.codes')}`
                : t('friend_codes.list_section.no_codes')}
            </h2>
            
            <div className="flex items-center gap-2">
              <label htmlFor="items-per-page" className="text-sm text-gray-600">{t('friend_codes.list_section.show')}</label>
              <select
                id="items-per-page"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm"
              >
                <option value="6">6</option>
                <option value="9">9</option>
                <option value="12">12</option>
                <option value="24">24</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {currentFriendCodes.map((code) => (
              <article 
                key={`${code.user_id}-${code.pokemon_id}`} 
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex flex-col h-full border border-gray-100"
              >
                <div className="p-6">
                  <Link 
                    to={getPathForLang(`/users/${code.username}`)}
                    className="flex items-center gap-4 mb-4 hover:opacity-80 transition-opacity"
                    title={`Voir le profil de ${code.username}`}
                  >
                    {code.avatar_url ? (
                      <img
                        src={code.avatar_url}
                        alt={`Avatar de ${code.username}`}
                        className="w-16 h-16 rounded-full object-cover ring-2 ring-indigo-50"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center ring-2 ring-indigo-100" aria-hidden="true">
                        <Users className="w-8 h-8 text-indigo-600" />
                      </div>
                    )}
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">{code.username}</h2>
                      <p className="text-sm text-gray-500 mt-1">{t('friend_codes.friend_card.trainer')}</p>
                    </div>
                  </Link>

                  <div className="mb-4 pt-3 border-t border-gray-100">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">{t('friend_codes.friend_card.friend_code')}</h3>
                    <CopyableFriendCode code={code.pokemon_id} />
                  </div>

                  {code.pokemon_id_description ? (
                    <div className="bg-gray-50 rounded-lg p-4 mt-3">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">{t('friend_codes.friend_card.description')}</h3>
                      <p className="text-sm text-gray-600 whitespace-pre-line">
                        {code.pokemon_id_description}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4 mt-3 text-center">
                      <p className="text-sm text-gray-400 italic">{t('friend_codes.friend_card.no_description')}</p>
                    </div>
                  )}
                  
                  <div className="mt-4 flex justify-end">
                    <Link 
                      to={getPathForLang(`/users/${code.username}`)}
                      className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                    >
                      {t('friend_codes.friend_card.view_profile')}
                    </Link>
                  </div>
                </div>
              </article>
            ))}

            {filteredFriendCodes.length === 0 && (
              <div className="col-span-full text-center py-12 bg-white rounded-lg shadow-md">
                <Users className="mx-auto h-12 w-12 text-gray-400" aria-hidden="true" />
                <h2 className="mt-2 text-sm font-medium text-gray-900">{t('friend_codes.list_section.no_results.title')}</h2>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm
                    ? t('friend_codes.list_section.no_results.no_match')
                    : t('friend_codes.list_section.no_results.empty')}
                </p>
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    {t('friend_codes.list_section.no_results.clear_button')}
                  </button>
                )}
              </div>
            )}
          </div>
        </section>
        
        {/* Contrôles de pagination */}
        {filteredFriendCodes.length > 0 && (
          <section className="flex justify-center mb-12">
            <nav className="flex items-center gap-2" aria-label="Pagination">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg border ${
                  currentPage === 1 
                    ? 'border-gray-200 text-gray-400 cursor-not-allowed' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                aria-label={t('friend_codes.pagination.prev_page')}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Affichage des numéros de page */}
              <div className="flex items-center">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  // Affichage pour 5 pages ou moins
                  if (totalPages <= 5) {
                    return (
                      <button
                        key={i}
                        onClick={() => goToPage(i + 1)}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          currentPage === i + 1
                            ? 'bg-indigo-600 text-white font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        aria-label={`${t('friend_codes.pagination.page')} ${i + 1}`}
                        aria-current={currentPage === i + 1 ? 'page' : undefined}
                      >
                        {i + 1}
                      </button>
                    );
                  } else {
                    // Logique complexe pour plus de 5 pages
                    let pageNum;
                    if (currentPage <= 3) {
                      // Au début, montrer 1, 2, 3, ..., totalPages
                      if (i < 3) {
                        pageNum = i + 1;
                      } else if (i === 3) {
                        return (
                          <span key="ellipsis1" className="w-10 h-10 flex items-center justify-center">
                            ...
                          </span>
                        );
                      } else {
                        pageNum = totalPages;
                      }
                    } else if (currentPage >= totalPages - 2) {
                      // À la fin, montrer 1, ..., totalPages-2, totalPages-1, totalPages
                      if (i === 0) {
                        pageNum = 1;
                      } else if (i === 1) {
                        return (
                          <span key="ellipsis2" className="w-10 h-10 flex items-center justify-center">
                            ...
                          </span>
                        );
                      } else {
                        pageNum = totalPages - (4 - i);
                      }
                    } else {
                      // Au milieu, montrer 1, ..., currentPage-1, currentPage, currentPage+1, ..., totalPages
                      if (i === 0) {
                        pageNum = 1;
                      } else if (i === 1) {
                        return (
                          <span key="ellipsis3" className="w-10 h-10 flex items-center justify-center">
                            ...
                          </span>
                        );
                      } else if (i === 4) {
                        return (
                          <span key="ellipsis4" className="w-10 h-10 flex items-center justify-center">
                            ...
                          </span>
                        );
                      } else if (i === 5) {
                        pageNum = totalPages;
                      } else {
                        pageNum = currentPage + (i - 3);
                      }
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          currentPage === pageNum
                            ? 'bg-indigo-600 text-white font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        aria-label={`${t('friend_codes.pagination.page')} ${pageNum}`}
                        aria-current={currentPage === pageNum ? 'page' : undefined}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                })}
              </div>

              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg border ${
                  currentPage === totalPages
                    ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                aria-label={t('friend_codes.pagination.next_page')}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </nav>
          </section>
        )}
        
        {/* Section Bénéfices */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('friend_codes.benefits_section.title')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm p-6 border-t-4 border-indigo-500">
              <Award className="w-10 h-10 text-indigo-500 mb-4" aria-hidden="true" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('friend_codes.benefits_section.miracle_draw.title')}</h3>
              <p className="text-gray-600">
                {t('friend_codes.benefits_section.miracle_draw.description')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-6 border-t-4 border-purple-500">
              <Gift className="w-10 h-10 text-purple-500 mb-4" aria-hidden="true" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('friend_codes.benefits_section.shop_tickets.title')}</h3>
              <p className="text-gray-600">
                {t('friend_codes.benefits_section.shop_tickets.description')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-6 border-t-4 border-pink-500">
              <Zap className="w-10 h-10 text-pink-500 mb-4" aria-hidden="true" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('friend_codes.benefits_section.direct_challenges.title')}</h3>
              <p className="text-gray-600">
                {t('friend_codes.benefits_section.direct_challenges.description')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-6 border-t-4 border-amber-500">
              <BarChart3 className="w-10 h-10 text-amber-500 mb-4" aria-hidden="true" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('friend_codes.benefits_section.mission_progress.title')}</h3>
              <p className="text-gray-600">
                {t('friend_codes.benefits_section.mission_progress.description')}
              </p>
            </div>
          </div>

          <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
            <div className="flex items-start gap-4">
              <Info className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <h3 className="text-lg font-semibold text-blue-800">{t('friend_codes.benefits_section.how_it_works.title')}</h3>
                <p className="mt-2 text-blue-700">
                  {t('friend_codes.benefits_section.how_it_works.description')}
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Guide d'étiquette */}
        <section className="mb-12 bg-gradient-to-r from-gray-50 to-indigo-50 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('friend_codes.etiquette.title')}</h2>
          
          <div className="prose prose-indigo max-w-none">
            <p>
              {t('friend_codes.etiquette.introduction')}
            </p>
            
            <ul className="mt-4 space-y-2">
              <li>
                <strong>{t('friend_codes.etiquette.tips.send_thanks')}</strong>
              </li>
              <li>
                <strong>{t('friend_codes.etiquette.tips.vary_decks')}</strong>
              </li>
              <li>
                <strong>{t('friend_codes.etiquette.tips.communicate')}</strong>
              </li>
              <li>
                <strong>{t('friend_codes.etiquette.tips.respect_privacy')}</strong>
              </li>
              <li>
                <strong>{t('friend_codes.etiquette.tips.help_beginners')}</strong>
              </li>
            </ul>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('friend_codes.faq.title')}</h2>
          
          <div className="space-y-4">
            <details className="bg-white rounded-lg shadow-sm">
              <summary className="p-4 font-medium cursor-pointer">{t('friend_codes.faq.how_to_add.question')}</summary>
              <div className="p-4 pt-0 text-gray-600">
                {t('friend_codes.faq.how_to_add.answer')}
              </div>
            </details>
            
            <details className="bg-white rounded-lg shadow-sm">
              <summary className="p-4 font-medium cursor-pointer">{t('friend_codes.faq.how_to_use_miracle.question')}</summary>
              <div className="p-4 pt-0 text-gray-600">
                {t('friend_codes.faq.how_to_use_miracle.answer')}
              </div>
            </details>
            
            <details className="bg-white rounded-lg shadow-sm">
              <summary className="p-4 font-medium cursor-pointer">{t('friend_codes.faq.how_to_get_tickets.question')}</summary>
              <div className="p-4 pt-0 text-gray-600">
                {t('friend_codes.faq.how_to_get_tickets.answer')}
              </div>
            </details>
            
            <details className="bg-white rounded-lg shadow-sm">
              <summary className="p-4 font-medium cursor-pointer">{t('friend_codes.faq.how_to_challenge.question')}</summary>
              <div className="p-4 pt-0 text-gray-600">
                {t('friend_codes.faq.how_to_challenge.answer')}
              </div>
            </details>
            
            <details className="bg-white rounded-lg shadow-sm">
              <summary className="p-4 font-medium cursor-pointer">{t('friend_codes.faq.how_many_friends.question')}</summary>
              <div className="p-4 pt-0 text-gray-600">
                {t('friend_codes.faq.how_many_friends.answer')}
              </div>
            </details>
          </div>
        </section>
        
        {/* Call to Action */}
        <section className="bg-indigo-600 text-white rounded-xl overflow-hidden shadow-lg">
          <div className="p-8 sm:p-10">
            <h2 className="text-3xl font-bold mb-4">{t('friend_codes.cta.title')}</h2>
            <p className="text-lg opacity-90 mb-6">
              {user 
                ? t('friend_codes.cta.logged_in')
                : t('friend_codes.cta.not_logged_in')
              }
            </p>
            <div>
              <Link
                to={user ? getPathForLang("/profile") : getPathForLang("/register")}
                className="inline-flex items-center bg-white text-indigo-600 hover:bg-indigo-50 px-6 py-3 rounded-lg font-semibold transition-colors shadow-sm"
              >
                {user ? t('friend_codes.cta.manage_button') : t('friend_codes.cta.register_button')}
              </Link>
            </div>
          </div>
        </section>
      </main>
    </PageLayout>
  );
}